const content = `
# System Architecture Overview

Flux PhD is part of the Vortx Flux platform. The frontend communicates through a platform API gateway to multiple backend services.

## Request Flow

\`\`\`
React Frontend (Firebase Hosting)
    |
    v
Platform API (Cloud Run) -- auth + routing
    |
    +---> PhD API (Cloud Run) -- simulations, forecasting, model management
    +---> Site DNA API (Cloud Run) -- scenario optimization
    +---> Flux EV API (Cloud Run) -- EV fleet optimization
    +---> Orchestrator (Cloud Run) -- multi-agent coordination
    +---> Solver (Cloud Run, Julia) -- LP/MILP optimization engine
\`\`\`

## Key Services

| Service | URL Pattern | Language | Purpose |
|---------|------------|----------|---------|
| **Frontend** | Firebase Hosting | React | UI shell, all domain web apps |
| **Platform API** | Cloud Run | Python/Flask | Authentication, routing, project management |
| **PhD API** | Cloud Run | Python/Flask | Topology simulation, forecasting pipeline |
| **Site DNA API** | Cloud Run | Python/Flask | Solar+storage scenario optimization |
| **Flux EV API** | Cloud Run | Python/Flask | EV fleet charging optimization |
| **Orchestrator** | Cloud Run | Python/Flask | Multi-agent ADMM/CG coordination |
| **Solver** | Cloud Run | Julia | HiGHS LP/MILP solver for optimization |

## Authentication

1. User logs in via Firebase Auth (frontend)
2. Frontend includes Firebase ID token in all API requests
3. Platform API validates the token and checks product access (e.g., \`flux_phd\`)
4. If authorized, Platform API proxies the request to the target backend service

## Data Storage

| Store | Technology | What It Holds |
|-------|-----------|--------------|
| **Firestore** | Named database: \`vortx-flux-site-dna\` | Projects, scenarios, simulation history |
| **ComStock Cache** | Local filesystem (PhD API) | Downloaded building profiles, trained models |
| **Cloud Storage** | GCS buckets | Large assets, exports |

## Frontend Architecture

The frontend is a monorepo with workspace packages:

\`\`\`
apps/flux-web/           -- Shell app (routing, auth, layout)
apps/flux-phd-web/       -- PhD components, hooks, pages
apps/site-dna-web/       -- Site DNA components
apps/flux-ev-web/        -- Flux EV components
apps/vortx-shared-ui/    -- Shared UI components, theme, typography
\`\`\`

The \`flux-web\` shell imports components from workspace packages and compiles them via craco (Create React App Configuration Override).

## Environment Variables

| Variable | Where Set | Purpose |
|----------|----------|---------|
| \`REACT_APP_API_URL\` | Build time (.env) | Platform API base URL |
| \`REACT_APP_PHD_API_URL\` | Build time (.env) | PhD API base URL (direct calls) |
| \`NREL_DEVELOPER_API_KEY\` | PhD API runtime | NREL API access for weather data |
`;

export default content;
